import { usePageMeta } from "@/hooks/usePageMeta";

// ─────────────────────────────────────────────────────────────
// 팔레트 미리보기 (내부용) — 세 후보의 "조합"을 한 화면에 나란히.
// 실제 사이트 brand 스케일은 건드리지 않고, 후보 hex를 직접 그려 비교한다.
// 내비게이션에 링크를 걸지 않는다. 주소 /팔레트 직접 입력 전용.
// ─────────────────────────────────────────────────────────────

interface PointTag {
  label: string;
  hex: string;
}

interface Candidate {
  name: string;
  sub: string;
  hex: string;
  contrast: string;
  points: PointTag[];
  /** 후보 3(코랄)에서 제한 배지 조정안을 보여줄 때 사용 */
  blockedAdjust?: { bg: string; fg: string; note: string };
}

// 판정 배지 3종 (의미색) — 세 후보 공통. 통과 초록 / 확인 주황 / 제한 빨강.
const verdicts = [
  { label: "통과", bg: "#dcfce7", fg: "#166534" },
  { label: "확인 필요", bg: "#fef3c7", fg: "#b45309" },
  { label: "제한", bg: "#fee2e2", fg: "#b91c1c" },
];

const candidates: Candidate[] = [
  {
    name: "후보 1 — 청록 Teal",
    sub: "차분한 청록",
    hex: "#0f766e",
    contrast: "5.5:1",
    points: [
      { label: "디지털", hex: "#e11d48" },
      { label: "제조", hex: "#65a30d" },
      { label: "서비스", hex: "#ea580c" },
    ],
  },
  {
    name: "후보 2 — 남색 Indigo",
    sub: "현재 적용 중 · Linear식 어두운 주조 · 신뢰·전문성",
    hex: "#4338ca",
    contrast: "7.9:1",
    points: [
      { label: "디지털", hex: "#ec4899" },
      { label: "제조", hex: "#06b6d4" },
      { label: "서비스", hex: "#f59e0b" },
    ],
  },
  {
    name: "후보 3 — 코랄 Coral",
    sub: "따뜻한 분홍빛 · 지원·배려 톤",
    hex: "#be123c",
    contrast: "6.3:1",
    points: [
      { label: "디지털", hex: "#14b8a6" },
      { label: "제조", hex: "#65a30d" },
      { label: "서비스", hex: "#f59e0b" },
    ],
    // 코랄(#be123c)은 붉은 분홍빛이라 제한 빨강(#b91c1c)과 가깝다.
    // 구분을 위해 제한 배지를 더 어두운 빨강(red-900)으로 낮춘 안.
    blockedAdjust: {
      bg: "#7f1d1d",
      fg: "#fee2e2",
      note: "제한 배지를 더 어둡게(red-900) 낮춘 조정안 — 빨강 계열 유지",
    },
  },
];

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
      {children}
    </p>
  );
}

function SampleCard({ candidate }: { candidate: Candidate }) {
  const adjust = candidate.blockedAdjust;

  return (
    <section className="rounded-lg border border-gray-200 bg-white overflow-hidden dark:border-gray-800 dark:bg-gray-900">
      {/* 후보 헤더 */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {candidate.name}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5 dark:text-gray-400">
              {candidate.sub}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
              <span
                className="inline-block w-3 h-3 rounded-sm align-middle mr-1.5"
                style={{ backgroundColor: candidate.hex }}
              />
              대표색 {candidate.hex}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
              흰 글자 대비 {candidate.contrast} (AA 통과)
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* 1차 버튼 (대표색) */}
        <div>
          <Label>1차 버튼 — 대표색</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="px-4 py-2.5 rounded-md text-sm font-bold text-white whitespace-nowrap"
              style={{ backgroundColor: candidate.hex }}
            >
              신청 시작하기
            </button>
            <button
              type="button"
              className="px-4 py-2.5 rounded-md text-sm font-bold border whitespace-nowrap"
              style={{ color: candidate.hex, borderColor: candidate.hex }}
            >
              2차 버튼
            </button>
          </div>
        </div>

        {/* 본문 글자 + 배경 (중립) */}
        <div>
          <Label>본문 글자 · 배경 — 중립 회색 4~6단</Label>
          <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 p-4 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-base font-bold text-gray-900 dark:text-gray-100">
              제목 텍스트 — 진한 글자
            </p>
            <p className="text-sm text-gray-700 mt-1 leading-relaxed dark:text-gray-300">
              본문 텍스트 — 중간 글자. 국비지원 과정 정보를 설명하는 문장이 들어가는
              자리입니다. 배경은 옅은 면, 구분선은 중립 회색으로 나뉩니다.
            </p>
            <p className="text-xs text-gray-500 mt-2 dark:text-gray-400">
              보조 텍스트 — 메타·기준일 등 옅은 글자
            </p>
          </div>
        </div>

        {/* 판정 배지 (의미색) */}
        <div>
          <Label>판정 배지 3종 — 의미색 (통과 초록 · 확인 주황 · 제한 빨강)</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {verdicts.map((v) => (
              <span
                key={v.label}
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap"
                style={{ backgroundColor: v.bg, color: v.fg }}
              >
                <i
                  className={
                    v.label === "통과"
                      ? "ri-checkbox-circle-fill"
                      : v.label === "확인 필요"
                      ? "ri-error-warning-fill"
                      : "ri-close-circle-fill"
                  }
                />
                {v.label}
              </span>
            ))}
          </div>

          {/* 후보 3 코랄 — 1차 버튼과 제한 배지 동시 노출 + 조정안 */}
          {adjust && (
            <div className="mt-3 rounded-md border border-dashed border-gray-300 p-4 dark:border-gray-600">
              <p className="text-xs font-bold text-gray-700 mb-3 dark:text-gray-300">
                ⚠ 코랄 대표색 vs 제한(빨강) 충돌 확인 — 버튼과 배지를 나란히
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2.5 rounded-md text-sm font-bold text-white whitespace-nowrap"
                  style={{ backgroundColor: candidate.hex }}
                >
                  1차 버튼 (코랄)
                </button>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap"
                  style={{ backgroundColor: verdicts[2].bg, color: verdicts[2].fg }}
                >
                  <i className="ri-close-circle-fill" />
                  제한 (기존)
                </span>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap"
                  style={{ backgroundColor: adjust.bg, color: adjust.fg }}
                >
                  <i className="ri-close-circle-fill" />
                  제한 (조정안)
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed dark:text-gray-400">
                {adjust.note}
              </p>
            </div>
          )}
        </div>

        {/* 분야 태그 (포인트색) */}
        <div>
          <Label>분야 태그 2~3개 — 포인트색</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {candidate.points.map((p) => (
              <span
                key={p.label}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap"
                style={{
                  color: p.hex,
                  borderColor: `${p.hex}66`,
                  backgroundColor: `${p.hex}1a`,
                }}
              >
                {p.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PalettePage() {
  usePageMeta({
    title: "팔레트 미리보기 — 다봄 (내부용)",
    description: "후보 3개의 색 조합을 비교하는 내부용 화면입니다.",
    path: "/팔레트",
  });

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6 md:py-14">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
          팔레트 미리보기
        </h1>
        <p className="text-sm text-gray-600 mt-2 leading-relaxed max-w-2xl dark:text-gray-400">
          내부용 비교 화면입니다. 세 후보의 "조합"(대표색·중립·의미색·포인트색)을
          스크롤 한 번으로 비교합니다. 확정 후 이 화면은 삭제합니다.
        </p>
      </header>

      <div className="space-y-10">
        {candidates.map((c) => (
          <SampleCard key={c.name} candidate={c} />
        ))}
      </div>
    </div>
  );
}